/*
  Partial model-number search.

  Shopify's storefront search only matches WHOLE words. "AMD Ryzen 7 9800X3D" is
  indexed as the single word "9800x3d", so a search for "9800" found nothing, and
  "x870" missed every X870E board. The `options[prefix]=last` parameter that is
  meant to fix this is ignored by the current search engine.

  This file fills the gap without touching any product:
    1. It downloads every product title once (templates/collection.search-index.liquid,
       i.e. /collections/all?view=search-index) and keeps the words that contain a
       digit -- model codes. Cached in the visitor's browser for CACHE_MS.
    2. Before a search runs, every typed word becomes an exact-match group using
       Shopify's OR syntax, and a word that is the START of a longer model code is
       widened to include it:
         bitfenix     ->  bitfenix OR bitfenix
         9800         ->  9800 OR 9800x3d
         ryzen 9800   ->  (ryzen OR ryzen) (9800 OR 9800x3d)
       WHY EVERY WORD: a plain query goes to Shopify's AI ("semantic") search, which
       pads the results with loosely related products and is not even consistent --
       "Bitfenix" returned 382, "bitfenix" 435 and "BITFENIX" 135 for the same 6
       BitFenix products. A query containing OR is matched exactly, word for word.
    2b. Exact matching has no typo tolerance, so when it finds NOTHING ("bitfnix",
       a plural, a word that is in no title) the customer's own words are re-run
       through Shopify's normal search instead (marked raw=1 in the URL).
    3. The search page and the dropdown show what the customer TYPED, not the widened
       query: the original travels in the URL as `oq` and is put back on screen.

  New products are covered automatically -- the list is rebuilt from the live catalogue.

  Used by: assets/predictive-search.js (dropdown) and the submit listener below
  (every search form on the site). Loaded from snippets/search-popup.liquid.
*/
(function () {
  'use strict';

  var CACHE_KEY = 'slb-search-words-v1';
  var CACHE_MS = 6 * 60 * 60 * 1000;  // refresh the word list every 6 hours
  var MIN_LEN = 3;                    // never widen "14" or "i5" -- far too many matches
  var MAX_COMPLETIONS = 8;            // most common completions kept per word
  var WAIT_MS = 2500;                 // longest a search waits for the word list

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var words = null;   // { "9800x3d": 3, "x870e": 2, ... } -- word -> number of titles
  var loading = null;

  function tokenize(text) {
    // Same split Shopify uses: "X870-PLUS" is two words, "9800X3D" is one.
    return String(text).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  }

  function buildIndex(titles) {
    var counts = {};
    titles.forEach(function (title) {
      tokenize(title).forEach(function (w) {
        if (/\d/.test(w)) counts[w] = (counts[w] || 0) + 1;
      });
    });
    return counts;
  }

  function fetchPage(page) {
    return fetch(root + 'collections/all?view=search-index&page=' + page, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      });
  }

  // Page 1 says how many pages there are; the rest are fetched in parallel
  // (~0.4s for the whole catalogue, against ~1.5s one after another).
  function fetchTitles() {
    return fetchPage(1).then(function (first) {
      var rest = [];
      for (var page = 2; page <= Math.min(first.pages, 40); page++) rest.push(fetchPage(page));
      return Promise.all(rest).then(function (pages) {
        return [first].concat(pages).reduce(function (all, data) { return all.concat(data.titles || []); }, []);
      });
    });
  }

  function load() {
    if (words) return Promise.resolve(words);
    if (loading) return loading;
    try {
      var cached = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (cached && cached.w && Date.now() - cached.t < CACHE_MS) {
        words = cached.w;
        return Promise.resolve(words);
      }
    } catch (e) {}
    loading = fetchTitles()
      .then(function (titles) {
        words = buildIndex(titles);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), w: words })); } catch (e) {}
        return words;
      })
      .catch(function () { return null; })
      .then(function (result) { loading = null; return result; });
    return loading;
  }

  // A word that could be the start of a model code ("9800", "x870", "124").
  function isModelStart(w) {
    return w.length >= MIN_LEN && /\d/.test(w);
  }

  function expandWith(index, query) {
    var q = String(query || '').trim();
    if (!q) return q;
    // Hand-written search syntax is left alone -- this also stops a query being widened twice.
    if (/[()":*]|\bOR\b|\bAND\b|\bNOT\b/.test(q)) return q;

    // Same split as Shopify: "i5-12400F" is the two words "i5" and "12400f".
    var groups = tokenize(q).map(function (w) {
      var completions = [];
      if (index && isModelStart(w)) {
        completions = Object.keys(index)
          .filter(function (k) { return k !== w && k.indexOf(w) === 0; })
          .sort(function (a, b) { return index[b] - index[a] || a.length - b.length; })
          .slice(0, MAX_COMPLETIONS);
      }
      // "w OR w" when there is nothing to add: the OR alone switches on exact matching.
      return [w].concat(completions.length ? completions : [w]).join(' OR ');
    });
    if (!groups.length) return q;   // e.g. a query in Arabic script -- leave it to Shopify
    return groups.length === 1
      ? groups[0]
      : groups.map(function (g) { return '(' + g + ')'; }).join(' ');
  }

  // Resolves to the rewritten query. Only words that look like model numbers need the
  // word list; if it is not available within WAIT_MS they are still searched exactly,
  // just not widened -- a search is never blocked by this file.
  function expand(query) {
    var q = String(query || '').trim();
    if (words || !tokenize(q).some(isModelStart)) return Promise.resolve(expandWith(words, q));
    var timeout = new Promise(function (resolve) { setTimeout(function () { resolve(null); }, WAIT_MS); });
    return Promise.race([load(), timeout]).then(function (index) { return expandWith(index, q); });
  }

  // Adds `name=value` to links back to the search page for query q (pagination, sort).
  function tagLink(link, q, name, value) {
    try {
      var url = new URL(link.getAttribute('href'), window.location.href);
      if (!/\/search\/?$/.test(url.pathname) || url.searchParams.get('q') !== q) return;
      url.searchParams.set(name, value);
      link.setAttribute('href', url.pathname + url.search);
    } catch (e) {}
  }

  function tagLinks(container, q, name, value) {
    container.querySelectorAll('a[href*="q="]').forEach(function (link) {
      tagLink(link, q, name, value);
    });
  }

  // Puts the customer's own words back wherever the widened query was printed.
  function restoreText(container, expanded, original) {
    if (!container || !expanded || !original || expanded === original) return;
    var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.indexOf(expanded) !== -1) {
        node.nodeValue = node.nodeValue.split(expanded).join(original);
      }
    }
    container.querySelectorAll('input[name="q"]').forEach(function (input) {
      if (input.value === expanded) input.value = original;
    });
    tagLinks(container, expanded, 'oq', original);
  }

  function isSearchUrl(href) {
    try { return /\/search\/?$/.test(new URL(href, window.location.href).pathname); } catch (e) { return false; }
  }

  // Every search form on the site. Bubble phase, so the dropdown's own submit handler
  // runs first -- when it has already handled the submit (a highlighted result), skip.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (event.defaultPrevented || !form || !form.getAttribute('action') || !isSearchUrl(form.action)) return;
    var input = form.querySelector('input[name="q"]');
    var original = input ? input.value.trim() : '';
    if (!original) return;
    event.preventDefault();
    expand(original).then(function (expanded) {
      var params = new URLSearchParams(new FormData(form));
      params.set('q', expanded);
      if (expanded !== original) params.set('oq', original); else params.delete('oq');
      window.location.href = form.action.split('?')[0] + '?' + params.toString();
    });
  });

  // Start fetching the word list as soon as someone focuses a search box.
  document.addEventListener('focusin', function (event) {
    var el = event.target;
    if (el && el.matches && el.matches('input[name="q"]')) load();
  });

  // The search results page.
  //  - Arrived through one of our forms (`oq` present): show the typed words again,
  //    or, if the exact search found nothing, re-run the typed words as a normal search.
  //  - Already a normal search (`raw`): leave it, and keep pagination links normal too.
  //  - Arrived any other way (a shared link, the browser bar): rewrite and reload once.
  (function onSearchPage() {
    if (!/\/search\/?$/.test(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    var q = params.get('q');
    if (!q || params.get('view')) return;
    if (params.get('raw')) {
      tagLinks(document.body, q, 'raw', '1');
      return;
    }
    var oq = params.get('oq');
    if (oq) {
      // main-search.liquid adds this class when the search returned no results.
      if (document.querySelector('.template-search--empty')) {
        params.set('q', oq);
        params.delete('oq');
        params.delete('page');
        params.set('raw', '1');
        window.location.replace(window.location.pathname + '?' + params.toString());
        return;
      }
      restoreText(document.body, q, oq);
      document.title = document.title.split(q).join(oq);
      return;
    }
    expand(q).then(function (expanded) {
      if (expanded === q.trim()) return;
      params.set('q', expanded);
      params.set('oq', q);
      window.location.replace(window.location.pathname + '?' + params.toString());
    });
  })();

  window.SLBSearch = { expand: expand, restoreText: restoreText };
})();
